import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, ArrowLeft, Save, Plus, ArrowUp, ArrowDown, Trash2, BookOpen } from 'lucide-react';
import { api } from '@/lib/api';
import { Workflow, WorkflowDefinition, WorkflowStep, WorkflowLink } from '@/types/workflow';
import { WorkflowStepEditor } from '@/components/workflows/WorkflowStepEditor';
import { PlanConfigEditor } from '@/components/workflows/PlanConfigEditor';
import { AIGenerationTab } from '@/components/workflows/AIGenerationTab';
import { ImageManagerTab } from '@/components/workflows/ImageManagerTab';
import { ImageGenerationDialog } from '@/components/workflows/ImageGenerationDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API_PATH = '/workflows';

const WORKFLOW_SCHEMA_EXAMPLE = `{
  "settings": {
    "forceAgentForFlow": false,
    "offlineRunnable": true
  },
  "steps": [
    {
      "id": "step1",
      "aim": "Understand user's goal",
      "question": "What would you like to accomplish?",
      "field": {
        "type": "singleChoice",
        "id": "goal",
        "label": "Choose your goal",
        "required": true,
        "options": [
          {
            "value": "plan",
            "label": "Plan something",
            "icon": "📅",
            "description": "Organize and schedule"
          },
          {
            "value": "learn",
            "label": "Learn something",
            "icon": "📚",
            "description": "Acquire new knowledge"
          }
        ]
      },
      "forceAgent": false,
      "transition": {
        "mode": "byAnswer",
        "map": {
          "plan": {
            "to": "mealDetails"
          },
          "learn": {
            "to": {
              "mode": "complete"
            }
          }
        }
      }
    },
    {
      "id": "mealDetails",
      "aim": "Collect meal preferences",
      "question": "Tell us about your meal",
      "fields": [
        {
          "type": "multiChoice",
          "id": "ingredients",
          "label": "Select ingredients",
          "required": true,
          "displayMode": "grid",
          "min": 1,
          "max": 10,
          "options": [
            {
              "value": "tomato",
              "label": "Tomato",
              "icon": "🍅"
            },
            {
              "value": "onion",
              "label": "Onion",
              "icon": "🧅"
            }
          ]
        },
        {
          "type": "number",
          "id": "servings",
          "label": "Number of servings",
          "required": true,
          "min": 1,
          "max": 10,
          "step": 1
        },
        {
          "type": "singleChoice",
          "id": "mealType",
          "label": "Meal type",
          "required": true,
          "options": [
            {
              "value": "breakfast",
              "label": "Breakfast",
              "icon": "🌅"
            },
            {
              "value": "lunch",
              "label": "Lunch",
              "icon": "☀️"
            },
            {
              "value": "dinner",
              "label": "Dinner",
              "icon": "🌙"
            }
          ]
        }
      ],
      "forceAgent": false,
      "transition": {
        "mode": "always",
        "default": {
          "mode": "complete"
        }
      }
    }
  ],
  "plan": {
    "title": "My Plan — {{today}}",
    "steps": [
      {
        "id": "summary",
        "aim": "Summarize the plan",
        "why": "Provides clarity on next steps",
        "agentTask": {
          "instruction": "Create a recipe for {{mealDetails.servings}} servings using {{mealDetails.ingredients}} for {{mealDetails.mealType}}",
          "outputType": "doc",
          "context": ["mealDetails.ingredients", "mealDetails.servings", "mealDetails.mealType"]
        }
      }
    ]
  },
  "links": []
}`;

// Helper function to convert Zod schema to readable TypeScript-like format
const formatZodSchema = (schema: any, indent = 0): string => {
  const spaces = '  '.repeat(indent);

  if (!schema || !schema._def) {
    return 'unknown';
  }

  const typeName = schema._def.typeName;

  switch (typeName) {
    case 'ZodString':
      return 'string';
    case 'ZodNumber':
      return 'number';
    case 'ZodBoolean':
      return 'boolean';
    case 'ZodArray':
      return `Array<${formatZodSchema(schema._def.type, indent)}>`;
    case 'ZodObject':
      const shape = schema._def.shape();
      const props = Object.keys(shape).map(key => {
        const isOptional = shape[key]._def.typeName === 'ZodOptional';
        const innerSchema = isOptional ? shape[key]._def.innerType : shape[key];
        return `${spaces}  ${key}${isOptional ? '?' : ''}: ${formatZodSchema(innerSchema, indent + 1)}`;
      }).join('\n');
      return `{\n${props}\n${spaces}}`;
    case 'ZodOptional':
      return `${formatZodSchema(schema._def.innerType, indent)} | undefined`;
    case 'ZodEnum':
      return schema._def.values.map((v: string) => `"${v}"`).join(' | ');
    case 'ZodLiteral':
      return `"${schema._def.value}"`;
    case 'ZodUnion':
    case 'ZodDiscriminatedUnion':
      return 'union type (see source code for details)';
    case 'ZodRecord':
      return `Record<string, ${formatZodSchema(schema._def.valueType, indent)}>`;
    case 'ZodAny':
      return 'any';
    default:
      return typeName;
  }
};

// Generate actual schema documentation from source
const WORKFLOW_SCHEMA_DEFINITION = `// Source: @ponder/artifacts/src/workflows/schemas/workflow.schema.ts

WorkflowDefinitionSchema = z.object({
  settings: {
    forceAgentForFlow: boolean
    offlineRunnable: boolean
  }
  steps: Array<WorkflowStepSchema>
  plan?: WorkflowPlanConfigSchema
  links: Array<WorkflowLinkSchema>
})

WorkflowStepSchema = z.object({
  id: string
  aim: string
  question: string
  field?: FormFieldSchema  // DEPRECATED: Use fields instead
  fields?: Array<FormFieldSchema>  // Multiple fields per step
  agentTask?: {
    instruction: string
    outputUI: "singleChoice" | "multiChoice" | "dateTime" | "textInput" | "number"
    context: Array<string>
  }
  forceAgent: boolean
  transition?: StepTransitionSchema
  links?: Array<WorkflowLinkSchema>
})

StepTransitionSchema = z.object({
  mode: "byAnswer" | "byGuard" | "always"
  map?: Record<string, TransitionDestinationSchema>
  elseIf?: Array<{
    guard: string
    to: TransitionTarget
  }>
  default?: TransitionTarget
  onOther?: OtherTransitionSchema
})

TransitionTarget =
  | string  // Step ID or workflow slug
  | { workflow: string, step?: string }  // Specific workflow step
  | { mode: "complete" }  // End workflow, create plan
  | { mode: "agent", instruction: string }  // Hand off to agent

FormFieldSchema = (discriminated union by type)
  | SingleChoiceField
  | MultiChoiceField
  | DateTimeField
  | TextInputField
  | NumberField

// Base properties for all fields
BaseFieldSchema = {
  id: string
  type: "singleChoice" | "multiChoice" | "dateTime" | "textInput" | "number"
  label: string
  description?: string
  required: boolean (default: false)
  placeholder?: string
}

SingleChoiceField = BaseFieldSchema & {
  type: "singleChoice"
  options: Array<FieldOption>
  showMore?: boolean
}

MultiChoiceField = BaseFieldSchema & {
  type: "multiChoice"
  options: Array<FieldOption>
  min?: number
  max?: number
  showMore?: boolean
  displayMode?: "list" | "grid"  // list = checkboxes, grid = card grid
}

DateTimeField = BaseFieldSchema & {
  type: "dateTime"
  includeTime: boolean (default: false)
  minDate?: string
  maxDate?: string
  allowUnknown: boolean (default: false)
}

TextInputField = BaseFieldSchema & {
  type: "textInput"
  multiline: boolean (default: false)
  maxLength?: number
}

NumberField = BaseFieldSchema & {
  type: "number"
  min?: number
  max?: number
  step?: number
}

FieldOption = {
  value: string
  label: string
  icon?: string
  description?: string
}

WorkflowPlanConfigSchema = z.object({
  title: string
  steps: Array<PlanStepSchema>
  agentGenerated?: boolean  // Default: false (offline)
})

PlanStepSchema = z.object({
  id: string
  aim: string
  why?: string
  from?: string  // Reference workflow step (e.g., "steps.ideas")
  agentTask?: {
    instruction: string  // Template with {{plan.stepId.value}} variables
    outputType: string  // Artifact type: 'doc' | 'cards' | etc.
    context: Array<string>  // Plan context (e.g., ["plan.pickMeal.selected"])
  }
  condition?: string
  complete?: string  // e.g., "onSelect", "onEdit", "manual"
})`;

const WorkflowEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('visual');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showSchemaDialog, setShowSchemaDialog] = useState(false);
  const [schemaDialogTab, setSchemaDialogTab] = useState('example');

  // All available workflows for linking
  const [allWorkflows, setAllWorkflows] = useState<Workflow[]>([]);

  // All available agents
  const [allAgents, setAllAgents] = useState<Array<{ id: string; name: string; slug: string }>>([]);

  // Form data
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [aim, setAim] = useState('');
  const [why, setWhy] = useState('');
  // Note: imageUrl is now stored in definition.imageUrl, not as a separate field
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [status, setStatus] = useState<'draft' | 'published' | 'archived' | 'deprecated'>('draft');
  const [isPublic, setIsPublic] = useState(false);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);

  // SEO Metadata
  const [seoMetaTitle, setSeoMetaTitle] = useState('');
  const [seoMetaDescription, setSeoMetaDescription] = useState('');
  const [seoFocusKeyword, setSeoFocusKeyword] = useState('');

  // Definition
  const [definition, setDefinition] = useState<WorkflowDefinition>({
    settings: {
      forceAgentForFlow: false,
      offlineRunnable: true
    },
    steps: [],
    links: []
  });

  const [jsonValue, setJsonValue] = useState('');
  const [cursorPosition, setCursorPosition] = useState({ line: 1, column: 1 });
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    fetchAllWorkflows();
    fetchAllAgents();
    if (isEditMode) {
      fetchWorkflow();
    } else {
      syncJsonFromDefinition();
    }
  }, [id]);

  // Auto-sync JSON when definition changes (only in visual mode)
  useEffect(() => {
    if (activeTab === 'visual') {
      syncJsonFromDefinition();
    }
  }, [definition, activeTab]);

  const fetchAllWorkflows = async () => {
    try {
      const response = await api.get(API_PATH);
      const data = response.data;
      if (data.success) {
        setAllWorkflows(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
    }
  };

  const fetchAllAgents = async () => {
    try {
      const response = await api.get('/agents');
      // API returns array directly, not wrapped in { success, data }
      const agents = Array.isArray(response.data) ? response.data : [];
      setAllAgents(agents);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    }
  };

  const fetchWorkflow = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`${API_PATH}/${id}`);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch workflow');
      }

      const workflow: Workflow = data.data;
      setTitle(workflow.title);
      setSlug(workflow.slug);
      setDescription(workflow.description || '');
      setAim(workflow.aim || '');
      setWhy(workflow.why || '');
      // imageUrl is now in definition.imageUrl
      setCategory(workflow.category || '');
      setTags(workflow.tags || []);
      setStatus(workflow.status);
      setIsPublic(workflow.isPublic || false);
      setAgentId(workflow.agentId || null);

      // Load SEO metadata
      const seoMeta = (workflow as any).seoMetadata || {};
      setSeoMetaTitle(seoMeta.metaTitle || '');
      setSeoMetaDescription(seoMeta.metaDescription || '');
      setSeoFocusKeyword(seoMeta.focusKeyword || '');

      // Merge definition with defaults for missing properties
      const mergedDefinition: WorkflowDefinition = {
        imageUrl: workflow.definition.imageUrl, // Preserve workflow cover image
        settings: {
          forceAgentForFlow: workflow.definition.settings?.forceAgentForFlow ?? false,
          offlineRunnable: workflow.definition.settings?.offlineRunnable ?? true
        },
        steps: workflow.definition.steps || [],
        links: workflow.definition.links || [],
        plan: workflow.definition.plan
      };
      setDefinition(mergedDefinition);
      syncJsonFromDefinition(mergedDefinition);
    } catch (err) {
      console.error('Failed to fetch workflow:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const syncJsonFromDefinition = (def: WorkflowDefinition = definition) => {
    setJsonValue(JSON.stringify(def, null, 2));
    setJsonError(null);
  };

  const syncDefinitionFromJson = () => {
    try {
      const parsed = JSON.parse(jsonValue);
      setDefinition(parsed);
      setJsonError(null);
      return true;
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON');
      return false;
    }
  };

  const handleSave = async () => {
    // Validate
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!slug.trim()) {
      setError('Slug is required');
      return;
    }

    // If on JSON tab, sync first
    if (activeTab === 'json') {
      if (!syncDefinitionFromJson()) {
        setError('Please fix JSON errors before saving');
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      // Build SEO metadata object (only include if at least one field is set)
      const seoMetadata = (seoMetaTitle || seoMetaDescription || seoFocusKeyword) ? {
        metaTitle: seoMetaTitle || undefined,
        metaDescription: seoMetaDescription || undefined,
        focusKeyword: seoFocusKeyword || undefined
      } : undefined;

      const payload = {
        title,
        slug,
        description,
        aim,
        why,
        imageUrl: definition.imageUrl, // Top-level cover image (can be string or AssetImage)
        category,
        tags,
        status,
        isPublic,
        agentId,
        definition,
        seoMetadata
      };

      console.log('[WorkflowEditor] Saving workflow with payload:', {
        definitionImageUrl: definition.imageUrl,
        imageUrlType: typeof definition.imageUrl,
        isAssetImage: definition.imageUrl && typeof definition.imageUrl === 'object',
        stepsCount: definition.steps.length,
        firstStepHasTransition: !!definition.steps[0]?.transition,
        firstStepTransition: definition.steps[0]?.transition,
        firstStepHasFields: !!definition.steps[0]?.fields,
        firstStepFields: definition.steps[0]?.fields,
        firstStepHasField: !!definition.steps[0]?.field,
        fullDefinition: definition
      });

      const response = isEditMode
        ? await api.put(`${API_PATH}/${id}`, payload)
        : await api.post(API_PATH, payload);

      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Failed to save workflow');
      }

      // Navigate to list or stay on edit page
      if (!isEditMode) {
        navigate(`/workflows/${data.data.id}/edit`);
      } else {
        // Refresh data
        await fetchWorkflow();
      }
    } catch (err) {
      console.error('Failed to save workflow:', err);
      setError(err instanceof Error ? err.message : 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    const newStep: WorkflowStep = {
      id: `step${definition.steps.length + 1}`,
      aim: '',
      question: '',
      forceAgent: false
    };

    setDefinition({
      ...definition,
      steps: [...definition.steps, newStep]
    });
  };

  const updateStep = (index: number, step: WorkflowStep) => {
    const steps = [...definition.steps];
    steps[index] = step;
    setDefinition({ ...definition, steps });
  };

  const deleteStep = (index: number) => {
    setDefinition({
      ...definition,
      steps: definition.steps.filter((_, i) => i !== index)
    });
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === definition.steps.length - 1)
    ) {
      return;
    }

    const steps = [...definition.steps];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = steps[index];
    if (temp && steps[newIndex]) {
      steps[index] = steps[newIndex];
      steps[newIndex] = temp;
    }
    setDefinition({ ...definition, steps });
  };

  const addLink = () => {
    const newLink: WorkflowLink = {
      rel: 'reco',
      to: '',
      label: ''
    };

    setDefinition({
      ...definition,
      links: [...definition.links, newLink]
    });
  };

  const updateLink = (index: number, link: WorkflowLink) => {
    const links = [...definition.links];
    links[index] = link;
    setDefinition({ ...definition, links });
  };

  const deleteLink = (index: number) => {
    setDefinition({
      ...definition,
      links: definition.links.filter((_, i) => i !== index)
    });
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const updateCursorPosition = () => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const cursorPos = textarea.selectionStart || 0;
    const textBeforeCursor = jsonValue.substring(0, cursorPos);
    const lines = textBeforeCursor.split('\n');
    const line = lines.length;
    const lastLine = lines[lines.length - 1];
    const column = lastLine ? lastLine.length + 1 : 1;

    setCursorPosition({ line, column });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const availableStepIds = definition.steps.map(s => s.id).filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/workflows')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditMode ? 'Edit Workflow' : 'Create Workflow'}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode ? 'Update workflow configuration' : 'Define a new workflow'}
            </p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Saving...' : 'Save Workflow'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="visual">Visual Editor</TabsTrigger>
          <TabsTrigger value="json">JSON Editor</TabsTrigger>
          <TabsTrigger value="ai">AI Generation</TabsTrigger>
          <TabsTrigger value="images">Images</TabsTrigger>
        </TabsList>

        {/* VISUAL EDITOR TAB */}
        <TabsContent value="visual" className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Personal Goal Setting"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="e.g., personal-goal-setting"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Short description of the workflow"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="aim">Aim</Label>
                  <Input
                    id="aim"
                    value={aim}
                    onChange={(e) => setAim(e.target.value)}
                    placeholder="What this workflow achieves"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="why">Why</Label>
                  <Input
                    id="why"
                    value={why}
                    onChange={(e) => setWhy(e.target.value)}
                    placeholder="Why users should use this"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="imageUrl">Image</Label>
                  <div
                    className="border rounded-md px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors"
                    onClick={() => setIsImageDialogOpen(true)}
                  >
                    {definition.imageUrl ? (
                      <div className="flex items-center gap-2">
                        {typeof definition.imageUrl === 'string' ? (
                          <span className="text-sm truncate">{definition.imageUrl}</span>
                        ) : (
                          <span className="text-sm">
                            Themed: {definition.imageUrl.masterPrompt?.substring(0, 40) || 'Image set'}
                            {definition.imageUrl.masterPrompt && definition.imageUrl.masterPrompt.length > 40 ? '...' : ''}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Click to add image...</span>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g., productivity"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="agent">Default Agent</Label>
                  <Select
                    value={agentId || 'none'}
                    onValueChange={(value) => setAgentId(value === 'none' ? null : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select an agent (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Agent</SelectItem>
                      {allAgents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    The default agent to use when running this workflow publicly
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add tag..."
                  />
                  <Button type="button" onClick={addTag}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((tag, idx) => (
                    <div key={idx} className="flex items-center gap-1 bg-muted border rounded px-2 py-1">
                      <span className="text-sm">{tag}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0"
                        onClick={() => removeTag(tag)}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={(value: any) => setStatus(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="deprecated">Deprecated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Public Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Visibility</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isPublic"
                  checked={isPublic}
                  onCheckedChange={(checked) => setIsPublic(!!checked)}
                />
                <Label htmlFor="isPublic">Public Workflow</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Public workflows are visible to all users and can be discovered in the workflow library.
              </p>
            </CardContent>
          </Card>

          {/* SEO Metadata */}
          <Card>
            <CardHeader>
              <CardTitle>SEO (Search Engine Optimization)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="seoMetaTitle">Meta Title</Label>
                <Input
                  id="seoMetaTitle"
                  value={seoMetaTitle}
                  onChange={(e) => setSeoMetaTitle(e.target.value)}
                  placeholder={`${title} | Ponder`}
                  maxLength={60}
                />
                <p className="text-sm text-muted-foreground">
                  Custom title for search results (max 60 chars). Leave empty to use default: "{title} | Ponder"
                </p>
                <p className="text-xs text-muted-foreground">
                  {seoMetaTitle.length}/60 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="seoMetaDescription">Meta Description</Label>
                <Textarea
                  id="seoMetaDescription"
                  value={seoMetaDescription}
                  onChange={(e) => setSeoMetaDescription(e.target.value)}
                  placeholder={description || `Use Ponder to ${title.toLowerCase()}`}
                  maxLength={160}
                  rows={3}
                />
                <p className="text-sm text-muted-foreground">
                  Custom description for search results (max 160 chars). Leave empty to use workflow description.
                </p>
                <p className="text-xs text-muted-foreground">
                  {seoMetaDescription.length}/160 characters
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="seoFocusKeyword">Focus Keyword</Label>
                <Input
                  id="seoFocusKeyword"
                  value={seoFocusKeyword}
                  onChange={(e) => setSeoFocusKeyword(e.target.value)}
                  placeholder="e.g., meal planning"
                />
                <p className="text-sm text-muted-foreground">
                  Primary keyword to optimize for (added to meta keywords tag)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="forceAgentForFlow"
                  checked={definition.settings.forceAgentForFlow}
                  onCheckedChange={(checked) =>
                    setDefinition({
                      ...definition,
                      settings: { ...definition.settings, forceAgentForFlow: !!checked }
                    })
                  }
                />
                <Label htmlFor="forceAgentForFlow">Force Agent for Flow</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="offlineRunnable"
                  checked={definition.settings.offlineRunnable}
                  onCheckedChange={(checked) =>
                    setDefinition({
                      ...definition,
                      settings: { ...definition.settings, offlineRunnable: !!checked }
                    })
                  }
                />
                <Label htmlFor="offlineRunnable">Offline Runnable</Label>
              </div>
            </CardContent>
          </Card>

          {/* Steps */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Workflow Steps</CardTitle>
                <Button onClick={addStep}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Step
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {definition.steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="absolute left-0 top-4 flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveStep(index, 'up')}
                      disabled={index === 0}
                      className="h-6 w-6 p-0"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moveStep(index, 'down')}
                      disabled={index === definition.steps.length - 1}
                      className="h-6 w-6 p-0"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="ml-10">
                    <WorkflowStepEditor
                      step={step}
                      onChange={(updated) => updateStep(index, updated)}
                      onDelete={() => deleteStep(index)}
                      availableSteps={availableStepIds.filter((_, i) => i < index)}
                      availableWorkflows={allWorkflows}
                    />
                  </div>
                </div>
              ))}

              {definition.steps.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <p className="text-muted-foreground">No steps yet</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={addStep}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add First Step
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Plan Configuration */}
          <PlanConfigEditor
            plan={definition.plan}
            onChange={(plan) => setDefinition({ ...definition, plan })}
            availableSteps={availableStepIds}
          />

          {/* Links */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Workflow Links</CardTitle>
                <Button onClick={addLink}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Link
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {definition.links.map((link, index) => (
                <Card key={index}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-medium">Link {index + 1}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteLink(index)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Relationship</Label>
                        <Select
                          value={link.rel}
                          onValueChange={(value: any) => updateLink(index, { ...link, rel: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="parent">Parent</SelectItem>
                            <SelectItem value="sibling">Sibling</SelectItem>
                            <SelectItem value="child">Child</SelectItem>
                            <SelectItem value="alt">Alternative</SelectItem>
                            <SelectItem value="reco">Recommendation</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Target Workflow</Label>
                        <Select
                          value={link.to}
                          onValueChange={(value) => updateLink(index, { ...link, to: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select workflow" />
                          </SelectTrigger>
                          <SelectContent>
                            {allWorkflows.map((wf) => (
                              <SelectItem key={wf.id} value={wf.slug}>
                                {wf.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Label</Label>
                        <Input
                          value={link.label}
                          onChange={(e) => updateLink(index, { ...link, label: e.target.value })}
                          placeholder="Link label"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label>Guard (Optional)</Label>
                        <Input
                          value={link.guard || ''}
                          onChange={(e) => updateLink(index, { ...link, guard: e.target.value })}
                          placeholder="Condition expression"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Auto (Optional)</Label>
                        <Input
                          value={link.auto || ''}
                          onChange={(e) => updateLink(index, { ...link, auto: e.target.value })}
                          placeholder="Auto-trigger expression"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {definition.links.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <p className="text-muted-foreground">No links yet</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={addLink}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add First Link
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* JSON EDITOR TAB */}
        <TabsContent value="json" className="space-y-4">
          {jsonError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{jsonError}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Workflow Definition (JSON)</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowSchemaDialog(true)}
                  >
                    <BookOpen className="h-4 w-4 mr-2" />
                    View Schema
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => syncJsonFromDefinition()}
                  >
                    Reset from Visual
                  </Button>
                  <Button
                    onClick={() => {
                      if (syncDefinitionFromJson()) {
                        setActiveTab('visual');
                      }
                    }}
                  >
                    Apply to Visual
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center mb-2 text-sm text-muted-foreground">
                <span>Line {cursorPosition.line}, Column {cursorPosition.column}</span>
              </div>
              <Textarea
                ref={textareaRef}
                value={jsonValue}
                onChange={(e) => setJsonValue(e.target.value)}
                onKeyUp={updateCursorPosition}
                onClick={updateCursorPosition}
                className="font-mono text-sm"
                rows={30}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI GENERATION TAB */}
        <TabsContent value="ai">
          <AIGenerationTab
            workflow={{
              id: id || '',
              title,
              slug,
              description,
              aim,
              why,
              imageUrl: definition.imageUrl, // imageUrl is now in definition
              category,
              tags,
              status,
              isPublic,
              definition,
              createdAt: '',
              updatedAt: '',
              userId: '',
              isTemplate: false,
              version: 1,
              metadata: {},
              usageCount: 0
            }}
            onWorkflowUpdate={(updatedWorkflow) => {
              setDefinition(updatedWorkflow.definition);
            }}
          />
        </TabsContent>

        {/* IMAGE MANAGER TAB */}
        <TabsContent value="images">
          <ImageManagerTab
            workflow={{
              id: id || '',
              title,
              slug,
              description,
              aim,
              why,
              imageUrl: definition.imageUrl, // imageUrl is now in definition
              category,
              tags,
              status,
              isPublic,
              definition,
              createdAt: '',
              updatedAt: '',
              userId: '',
              isTemplate: false,
              version: 1,
              metadata: {},
              usageCount: 0
            }}
            onWorkflowUpdate={(updatedWorkflow) => {
              // imageUrl is now in definition
              setDefinition(updatedWorkflow.definition);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Schema Dialog */}
      <Dialog open={showSchemaDialog} onOpenChange={setShowSchemaDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Workflow Definition Schema</DialogTitle>
            <DialogDescription>
              View example workflows and schema definitions
            </DialogDescription>
          </DialogHeader>

          <Tabs value={schemaDialogTab} onValueChange={setSchemaDialogTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="example">Example</TabsTrigger>
              <TabsTrigger value="schema">Schema Definition</TabsTrigger>
            </TabsList>

            <TabsContent value="example" className="flex-1 overflow-y-auto mt-4">
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg">
                  <pre className="font-mono text-xs overflow-x-auto whitespace-pre">
                    {WORKFLOW_SCHEMA_EXAMPLE}
                  </pre>
                </div>
            <div className="space-y-2 text-sm">
              <h4 className="font-semibold">Single vs Multiple Fields:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><code className="bg-muted px-1 py-0.5 rounded">field</code> - Single field (deprecated, use for backward compatibility)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">fields</code> - Array of fields displayed together on one screen. Each field has unique id for referencing in plans</li>
              </ul>
            </div>
            <div className="space-y-2 text-sm">
              <h4 className="font-semibold">Field Types:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><code className="bg-muted px-1 py-0.5 rounded">singleChoice</code> - Radio buttons with options</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">multiChoice</code> - Checkboxes (list) or card grid (displayMode: 'grid') with min/max selection</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">dateTime</code> - Date/time picker</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">textInput</code> - Single or multi-line text</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">number</code> - Slider input with min/max/step</li>
              </ul>
            </div>
            <div className="space-y-2 text-sm">
              <h4 className="font-semibold">Option Properties:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><code className="bg-muted px-1 py-0.5 rounded">value</code> - Internal identifier (required)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">label</code> - Display text (required)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">icon</code> - Emoji or icon (optional)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">description</code> - Help text (optional)</li>
              </ul>
            </div>
            <div className="space-y-2 text-sm">
              <h4 className="font-semibold">MultiChoice Properties:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><code className="bg-muted px-1 py-0.5 rounded">displayMode</code> - 'list' (default checkboxes) or 'grid' (card grid with search)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">min</code> - Minimum selections required (optional)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">max</code> - Maximum selections allowed (optional)</li>
              </ul>
            </div>
            <div className="space-y-2 text-sm">
              <h4 className="font-semibold">Number Field Properties:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><code className="bg-muted px-1 py-0.5 rounded">min</code> - Minimum value (optional, defaults to 0)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">max</code> - Maximum value (optional, defaults to 100)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">step</code> - Increment step (optional, defaults to 1)</li>
              </ul>
            </div>
            <div className="space-y-2 text-sm">
              <h4 className="font-semibold">Transitions:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><code className="bg-muted px-1 py-0.5 rounded">mode: "byAnswer"</code> - Navigate based on field answer using map</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">mode: "byGuard"</code> - Navigate based on conditional guards (elseIf)</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">mode: "always"</code> - Always navigate to default destination</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">map</code> - Object mapping answer values to destinations</li>
                <li><code className="bg-muted px-1 py-0.5 rounded">default</code> - Fallback destination or {"{"}"mode": "complete"{"}"} to end workflow</li>
              </ul>
            </div>
            <div className="space-y-2 text-sm">
              <h4 className="font-semibold">Transition Destinations:</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>String step ID: <code className="bg-muted px-1 py-0.5 rounded">"stepId"</code> - Navigate to another step</li>
                <li>Complete workflow: <code className="bg-muted px-1 py-0.5 rounded">{"{"}mode: 'complete'{"}"}</code> - End workflow and create plan</li>
                <li>Workflow slug: <code className="bg-muted px-1 py-0.5 rounded">"wf.slug.entry"</code> - Navigate to another workflow</li>
                <li>Agent handoff: <code className="bg-muted px-1 py-0.5 rounded">{"{"}mode: 'agent', instruction: '...'{"}"}</code></li>
              </ul>
            </div>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(WORKFLOW_SCHEMA_EXAMPLE);
                  }}
                  className="w-full"
                >
                  Copy Example to Clipboard
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="schema" className="flex-1 overflow-y-auto mt-4">
              <div className="space-y-4">
                <div className="bg-muted p-4 rounded-lg">
                  <pre className="font-mono text-xs overflow-x-auto whitespace-pre">
                    {WORKFLOW_SCHEMA_DEFINITION}
                  </pre>
                </div>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(WORKFLOW_SCHEMA_DEFINITION);
                  }}
                  className="w-full"
                >
                  Copy Schema to Clipboard
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Image Generation Dialog */}
      <ImageGenerationDialog
        open={isImageDialogOpen}
        onOpenChange={setIsImageDialogOpen}
        currentImage={definition.imageUrl}
        onImageSelect={(image) => {
          console.log('[WorkflowEditorPage] Image selected:', image);
          setDefinition({ ...definition, imageUrl: image });
        }}
        title="Workflow Cover Image"
      />
    </div>
  );
};

export default WorkflowEditorPage;
